
import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination"

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // No pagination needed for 1 page or less
  if (totalPages <= 1) {
    return null;
  }

  // Function to determine which page numbers to show
  const getPageNumbers = () => {
    if (totalPages <= 5) {
      // If 5 or fewer pages, show all
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always include first, last, current page, and one page on each side of current
    const pages = [1];
    
    // Start page
    const startPage = Math.max(2, currentPage - 1);
    // End page
    const endPage = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis if there's a gap between 1 and startPage
    if (startPage > 2) {
      pages.push(-1); // -1 represents an ellipsis
    }
    
    // Add the range of pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if there's a gap between endPage and totalPages
    if (endPage < totalPages - 1) {
      pages.push(-2); // -2 represents another ellipsis (with a different key)
    }
    
    // Add the last page
    pages.push(totalPages);
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Pagination>
      <PaginationContent className="gap-1">
        <PaginationItem>
          <PaginationPrevious
            onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
            className={`
              bg-[#0F1726] border border-[#FFC745]/30 text-[#FFC745] 
              hover:bg-[#FFC745]/20 hover:border-[#FFC745] 
              ${currentPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            `}
          />
        </PaginationItem>
        
        {pageNumbers.map((page, index) => (
          <PaginationItem key={`page-${page}-${index}`}>
            {page < 0 ? (
              <PaginationEllipsis className="text-[#FFC745]" />
            ) : (
              <PaginationLink
                isActive={currentPage === page}
                onClick={() => onPageChange(page)}
                className={`
                  cursor-pointer transition-all
                  ${currentPage === page 
                    ? 'bg-[#FFC745] text-black font-bold border-[#FFC745] hover:bg-[#FFC745]/90' 
                    : 'bg-[#0F1726] text-[#FFC745] border border-[#FFC745]/30 hover:bg-[#FFC745]/20 hover:border-[#FFC745]'
                  }
                `}
              >
                {page}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
            className={`
              bg-[#0F1726] border border-[#FFC745]/30 text-[#FFC745] 
              hover:bg-[#FFC745]/20 hover:border-[#FFC745]
              ${currentPage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            `}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
